import _ from 'lodash';
import React, {FC, Fragment, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AnswerFolderModel} from '../../../../models/api/answerFolderModel';
import {AnswerFolderDropdownItemWrapper} from './answerFolderDropdownItemWrapper';

/*
 * Props and Types.
 */

export interface AnswerFolderSearchResultsDropdownItemProps {
  folder: AnswerFolderModel;
  fullFolderPathNames: ReadonlyArray<string>;
  index: number;
  selectedFolder: AnswerFolderModel | undefined;
  onFolderSelect: HandlerOf<number | undefined>;
}

/*
 * Styles.
 */

interface FolderNameStyleProps {
  $isLast: boolean;
  $isTooltip: boolean;
}

const StyledFolderNameSpan = styled.span<FolderNameStyleProps>`
  direction: ltr;
  color: ${(p) => (p.$isLast || p.$isTooltip ? 'inherit' : p.theme.greys.shade60)};
  font-weight: ${(p) => (p.$isLast || p.$isTooltip ? 'inherit' : fontWeights.normal)};
`;

const StyledChevronSpan = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin: 0 3px;
`;

/*
 * Component.
 */

interface FolderPathSectionInfo {
  folderId: number;
  folderName: string;
}

export const AnswerFolderSearchResultsDropdownItem: FC<AnswerFolderSearchResultsDropdownItemProps> = (
  props,
) => {
  const {folder, fullFolderPathNames, index, selectedFolder, onFolderSelect} = props;

  //  A folder hierarchy of: ParentFolder(id=1) > ChildFolder(id=2) > GrandchildFolder(id=3)
  //  Yields the object:
  //    [
  //      {folderId: 1, folderName: 'ParentFolder'},
  //      {folderId: 2, folderName: 'ChildFolder'},
  //      {folderId: 3, folderName: 'GrandchildFolder'},
  //    ]
  const folderPathSections: ReadonlyArray<FolderPathSectionInfo> = useMemo(() => {
    const fullFolderPathIds = [...folder.folderPath, folder.id];
    return _.zipWith(fullFolderPathIds, fullFolderPathNames, (folderId, folderName) => ({
      folderId,
      folderName,
    }));
  }, [folder, fullFolderPathNames]);

  const renderBreadcrumbsForTooltip = useCallback(
    () => renderFolderBreadcrumbs(folderPathSections, true),
    [folderPathSections],
  );

  return (
    <AnswerFolderDropdownItemWrapper
      folder={folder}
      index={index}
      isChecked={selectedFolder?.id === folder.id}
      onFolderSelect={onFolderSelect}
      renderTooltip={renderBreadcrumbsForTooltip}
    >
      <TooltipOverflowCondition isRightToLeft>
        {renderFolderBreadcrumbs(folderPathSections)}
      </TooltipOverflowCondition>
    </AnswerFolderDropdownItemWrapper>
  );
};

// e.g. "...derName1 > ParentFolderName2 > ActualFolderName"
function renderFolderBreadcrumbs(
  folderPathSections: ReadonlyArray<FolderPathSectionInfo>,
  isTooltip: boolean = false,
) {
  return folderPathSections.map((folderPathSection, index, array) => {
    const isLast = index === array.length - 1;

    return (
      <Fragment key={`${folderPathSection.folderId}`}>
        <StyledFolderNameSpan $isLast={isLast} $isTooltip={isTooltip}>
          {folderPathSection.folderName}
        </StyledFolderNameSpan>
        {!isLast && renderRightChevron()}
      </Fragment>
    );
  });
}

function renderRightChevron() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledChevronSpan>
          <Icon name="chevronRightTiny" color={theme.greys.shade60} />
        </StyledChevronSpan>
      )}
    </UserThemeConsumer>
  );
}
